import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import styled from "styled-components";
import { List, FlexboxGrid, Grid, Loader, Popover, Whisper } from "rsuite";
import { GlassCard } from "../../../../ui";
import { colors } from "../../../../ui/theme/colors";
import { shadows } from "../../../../ui/theme/shadows";
import "rsuite/dist/rsuite.min.css";
import { DarkModeContext } from "../../../../../appContexts/DarkModeContext";
import { SectionTitleFC } from "../../_helpers/SectionTitleFC";
import { useHistory } from "react-router-dom";
import { renderCount } from "../../../../../appHelpers/appLoggers";
import LocationIcon from "@rsuite/icons/Location";
import { useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import { appTheme } from "../../../../../appHelpers/appTheme";
import { _FilterEquipmentFC } from "./_helpers/dashboardFilters/_FilterEquipmentFC";
import { DateTime, Interval } from "luxon";
import { AlertListFC } from "./_helpers/AlertListFC";
import { UserContext } from "../../_context/UserContext";
import NoticeIcon from "@rsuite/icons/Notice";
import { MachinesContext } from "./_context/MachinesContext";
import RemindFillIcon from "@rsuite/icons/RemindFill";
import ImportIcon from "@rsuite/icons/Import";

export const HHMTableFC = ({
  machines,
  alertConfigs,
  networkIssues,
  dashboardAlerts,
}) => {
  // renderCount('MachinesTableFC');
  const history = useHistory();
  const { user, userSiteGroups } = useContext(UserContext);
  const { getAccessTokenSilently } = useAuth0();
  const [displayMachines, setDisplayMachines] = useState(machines);
  const [displayCustomers, setDisplayCustomers] = useState([]);
  const [displayFileDatetimes, setDisplayFileDatetimes] = useState([]);
  const { darkMode } = useContext(DarkModeContext);
  const clickStartRef = useRef(null);

  const {
    loadingDisplayEvents,
    setLoadingDisplayEvents,
    displayEvents,
    getRecentEvents,
    getDisplayEventsError,
    frozenSystems,
  } = useContext(MachinesContext);

  const speakerElement = (system) => (
    <Popover style={{ width: "550px" }}>
      <AlertListFC user={user} system={system} />
    </Popover>
  );

  useEffect(() => {
    getRecentEvents();
  }, [machines]);

  useEffect(() => {
    setDisplayCustomers(
      [...new Set(displayMachines.map((item) => item.customer_name))].sort(),
    );
  }, [displayMachines]);

  const handleSiteClick = (siteName, customerName) => {
    // Navigate to a site-specific view - you can customize this URL pattern
    history.push(
      `/customer/${encodeURIComponent(customerName)}/site/${encodeURIComponent(siteName)}`,
    );
  };

  // Memoize grouped systems to avoid recalculating on every render
  const groupedSystemsByCustomer = useMemo(() => {
    const cache = {};

    displayCustomers.forEach((customerName) => {
      const customerSystems = displayMachines?.filter(
        (item) => item.customer_name === customerName,
      );
      const result = [];

      // Get all site groups the user has access to for this customer
      const userCustomerSiteGroups =
        userSiteGroups?.filter(
          (userGroup) => userGroup.customer_name === customerName,
        ) || [];

      // Create set of sites that are in user's AUTHORIZED site groups
      const sitesInAuthorizedGroups = new Set();

      // Only process if user has explicit site group access
      if (userCustomerSiteGroups.length > 0) {
        // Group user's site groups and their systems
        const siteGroupMap = {};
        userCustomerSiteGroups.forEach((userGroup) => {
          if (!siteGroupMap[userGroup.site_group_name]) {
            siteGroupMap[userGroup.site_group_name] = new Set();
          }
          if (userGroup.site_name) {
            siteGroupMap[userGroup.site_group_name].add(userGroup.site_name);
            sitesInAuthorizedGroups.add(userGroup.site_name);
          }
        });

        // Add systems grouped by authorized site groups first
        Object.entries(siteGroupMap).forEach(([groupName, sites]) => {
          const groupSystems = customerSystems.filter((system) =>
            sites.has(system.site_name),
          );
          if (groupSystems.length > 0) {
            result.push({
              type: "group",
              site_group_name: groupName,
              systems: groupSystems,
            });
          }
        });
      }

      // Get individual systems (sites user has access to but NOT through a site group)
      const individualSystems = customerSystems.filter(
        (system) => !sitesInAuthorizedGroups.has(system.site_name),
      );

      // Add individual systems last
      if (individualSystems.length > 0) {
        result.push({
          type: "individual",
          systems: individualSystems,
        });
      }

      cache[customerName] = result;
    });

    return cache;
  }, [displayMachines, displayCustomers, userSiteGroups]);

  const getGroupedSystemsForCustomer = useCallback(
    (customerName) => {
      return groupedSystemsByCustomer[customerName] || [];
    },
    [groupedSystemsByCustomer],
  );

  return displayMachines ? (
    <MachineTableSC darkMode={darkMode}>
      {/* Filter Control Header Row */}
      <_FilterEquipmentFC
        machines={machines}
        displayMachines={displayMachines}
        setDisplayMachines={setDisplayMachines}
        alertConfigs={alertConfigs}
        networkIssues={networkIssues}
        frozenSystems={frozenSystems}
        dashboardAlerts={dashboardAlerts}
      />

      {window.screen.width >= 768 ? (
        <List size="sm" hover>
          {/* Header For Equipment List */}
          {displayCustomers.map((customer, customerIndex) => (
            <CustomerCard
              key={customer}
              darkMode={darkMode}
              style={{ marginTop: customerIndex === 0 ? "16px" : undefined }}
            >
              {/* Customer Header */}
              <CustomerHeader>
                <CustomerText> {customer} </CustomerText>
              </CustomerHeader>

              {customerIndex === 0 ? <RowHeader /> : <></>}

              {(() => {
                const groupedSystems = getGroupedSystemsForCustomer(customer);
                const hasGroups = groupedSystems.some(
                  (group) => group.type === "group",
                );

                return groupedSystems.flatMap((group, groupIndex) => [
                  // Add header for site groups and individual sites (only show Individual Sites header if there are also groups)
                  group.type === "group" ||
                  (group.type === "individual" && hasGroups) ? (
                    <SiteGroupHeader
                      key={`${customer}-${group.type}-${group.site_group_name || "individual"}`}
                    >
                      <SiteGroupText darkMode={darkMode}>
                        {group.type === "group"
                          ? `${group.site_group_name} (${group.systems.length})`
                          : `Individual Sites (${group.systems.length})`}
                      </SiteGroupText>
                    </SiteGroupHeader>
                  ) : null,
                  // Add all systems in this group
                  ...group.systems.map((machine) => (
                    <List.Item
                      value={machine.id}
                      key={machine.id + `_hhm`}
                      index={customerIndex + 1 + `_hhm`}
                      size="sm"
                    >
                      {/* Checks whether there's an unresolved issue */}
                      {networkIssues ? (
                        networkIssues[0]?.filter(
                          (issue) =>
                            issue.id === machine.id &&
                            !issue.resolved &&
                            issue.status === "active" &&
                            issue.processor_type === "HHM",
                        ).length > 0 ? (
                          <Whisper
                            placement="top"
                            trigger="hover"
                            speaker={
                              <Popover style={{ width: "400px" }}>
                                <div style={{ padding: "8px" }}>
                                  <strong>Unresolved Issues:</strong>
                                  {networkIssues[0]
                                    ?.filter(
                                      (issue) =>
                                        issue.id === machine.id &&
                                        !issue.resolved &&
                                        issue.status === "active" &&
                                        issue.processor_type === "HHM",
                                    )
                                    .map((issue, idx) => (
                                      <div
                                        key={idx}
                                        style={{
                                          marginTop: "8px",
                                          paddingTop: "8px",
                                          borderTop:
                                            idx > 0
                                              ? "1px solid #e0e0e0"
                                              : "none",
                                        }}
                                      >
                                        <div>
                                          <strong>Type:</strong> {issue.type}
                                        </div>
                                        {issue.description && (
                                          <div style={{ marginTop: "4px" }}>
                                            <strong>Description:</strong>{" "}
                                            {issue.description}
                                          </div>
                                        )}
                                        {issue.notes && (
                                          <div style={{ marginTop: "4px" }}>
                                            <strong>Notes:</strong>{" "}
                                            {issue.notes}
                                          </div>
                                        )}
                                        {issue.created_at && (
                                          <div
                                            style={{
                                              marginTop: "4px",
                                              fontSize: "0.85em",
                                              color: "grey",
                                            }}
                                          >
                                            Reported:{" "}
                                            {DateTime.fromISO(
                                              issue.created_at,
                                            ).toFormat("LLL dd, yyyy")}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                </div>
                              </Popover>
                            }
                          >
                            <FlexboxGrid
                              style={{
                                backgroundColor: "#fffaf2",
                                paddingLeft: 10,
                                marginBottom: 5,
                                cursor: "pointer",
                              }}
                            >
                              <FlexboxGrid.Item colspan={1}>
                                <RemindFillIcon color="#ffb300" />
                              </FlexboxGrid.Item>
                              <FlexboxGrid.Item
                                colspan={23}
                                style={{ color: "grey" }}
                              >
                                Warning: This system has an unresolved reported
                                issue.
                              </FlexboxGrid.Item>
                            </FlexboxGrid>
                          </Whisper>
                        ) : null
                      ) : null}
                      <SystemRow
                        onMouseDown={(event) => {
                          clickStartRef.current = event.timeStamp; // Record the timestamp of the mouse down event
                        }}
                        onMouseUp={(event) => {
                          const clickEnd = event.timeStamp; // Get the timestamp of the mouse up event
                          const clickDuration =
                            clickEnd - (clickStartRef.current || 0); // Calculate the duration of the click
                          const modalElement =
                            document.getElementById("control-id-hover");

                          if (
                            clickDuration < 200 &&
                            !modalElement?.contains(event.target)
                          ) {
                            history.push(`/system/hhm/${machine.id}`); // Navigate to the next page if the click was short
                          }
                        }}
                      >
                        <FlexboxGrid>
                          <FlexboxGrid.Item colspan={2} style={itemGridStyle}>
                            <ItemContent>
                              {machine.cus_sys_id ? (
                                <>
                                  <ItemMainText>
                                    {" "}
                                    {machine.cus_sys_id}{" "}
                                  </ItemMainText>
                                  <ItemSubText> {machine.id} </ItemSubText>
                                </>
                              ) : (
                                <ItemMainText> {machine.id} </ItemMainText>
                              )}
                            </ItemContent>
                          </FlexboxGrid.Item>

                          <FlexboxGrid.Item colspan={7} style={itemGridStyle}>
                            <ItemContent>
                              <ItemMainText>
                                <LocationIcon /> {machine.site_name}
                              </ItemMainText>
                              {/* <ItemSubText>
                                       Room: {system.room ? system.room : 'N/A'}
                                    </ItemSubText> */}
                              <ItemSubText>
                                {machine.state} · {machine.city}{" "}
                              </ItemSubText>
                            </ItemContent>
                          </FlexboxGrid.Item>

                          <FlexboxGrid.Item colspan={3} style={itemGridStyle}>
                            <ItemContent>
                              <ItemMainText>
                                {machine.manufacturer}
                              </ItemMainText>
                              <ItemSubText>
                                {machine.model ? machine.model : "N/A"}
                              </ItemSubText>
                            </ItemContent>
                          </FlexboxGrid.Item>

                          <FlexboxGrid.Item colspan={2} style={itemGridStyle}>
                            <ItemContent>
                              <ItemMainText>{machine.modality}</ItemMainText>
                            </ItemContent>
                          </FlexboxGrid.Item>

                          <FlexboxGrid.Item colspan={1} style={itemGridStyle}>
                            {alertConfigs?.filter(
                              (config) =>
                                config.system_id === machine.id &&
                                config.user_id.toLowerCase() ===
                                  user?.email_address.toLowerCase() &&
                                (config.pg_table.includes("log") ||
                                  config.pg_table.includes("hhm")) &&
                                config.enabled,
                            )?.length !== 0 ? (
                              <Whisper
                                preventOverflow
                                placement="left"
                                trigger="hover"
                                controlId="control-id-hover"
                                speaker={speakerElement(machine)}
                                enterable
                              >
                                <NoticeIcon color={appTheme.avante_dark_blue} />
                              </Whisper>
                            ) : alertConfigs?.filter(
                                (config) =>
                                  config.system_id === machine.id &&
                                  config.user_id.toLowerCase() ===
                                    user?.email_address.toLowerCase() &&
                                  (config.pg_table.includes("log") ||
                                    config.pg_table.includes("hhm")),
                              ).length !== 0 ? (
                              <Whisper
                                preventOverflow
                                placement="left"
                                trigger="hover"
                                controlId="control-id-hover"
                                speaker={speakerElement(machine)}
                                enterable
                              >
                                <NoticeIcon color="grey" />
                              </Whisper>
                            ) : (
                              <NoticeIcon color="red" />
                            )}
                          </FlexboxGrid.Item>

                          <FlexboxGrid.Item colspan={6} style={itemGridStyle}>
                            <RecentEventBox>
                              {loadingDisplayEvents ? (
                                <Loader speed="fast" />
                              ) : (
                                displayEvents
                                  ?.filter(
                                    (events) => events.system_id === machine.id,
                                  )
                                  ?.map((event) =>
                                    event?.name === "system_scan_seconds" ? (
                                      <RecentEventItem>
                                        <ItemSubText>
                                          Sys Scan Secs:
                                        </ItemSubText>
                                        <ItemMainText>
                                          {event.value}{" "}
                                          <EventUnits>secs</EventUnits>
                                        </ItemMainText>
                                      </RecentEventItem>
                                    ) : event?.name === "scan_seconds" ? (
                                      <RecentEventItem>
                                        <ItemSubText>Tube Usage:</ItemSubText>
                                        <ItemMainText>
                                          {event.value}{" "}
                                          <EventUnits>secs</EventUnits>
                                        </ItemMainText>
                                      </RecentEventItem>
                                    ) : event?.name === "power_on" ? (
                                      // <RecentEventItem>
                                      //    <ItemSubText>Power On:</ItemSubText>
                                      //    <ItemMainText>{event.value} <EventUnits>hours</EventUnits></ItemMainText>
                                      // </RecentEventItem>
                                      <></>
                                    ) : event?.name === "software_version" ? (
                                      <RecentEventItem>
                                        <ItemSubText>
                                          Software Version:
                                        </ItemSubText>
                                        <ItemMainText>
                                          {event.value}{" "}
                                        </ItemMainText>
                                      </RecentEventItem>
                                    ) : event?.name === "tube_type" ? (
                                      <RecentEventItem>
                                        <ItemSubText>Tube Type:</ItemSubText>
                                        <ItemMainText>
                                          {event.value}{" "}
                                        </ItemMainText>
                                      </RecentEventItem>
                                    ) : event?.name === "tube_serial_no" ? (
                                      <RecentEventItem>
                                        <ItemSubText>
                                          Tube Serial #:
                                        </ItemSubText>
                                        <ItemMainText>
                                          {event.value}{" "}
                                        </ItemMainText>
                                      </RecentEventItem>
                                    ) : (
                                      <RecentEventItem>
                                        <ItemSubText>{event.name}</ItemSubText>
                                        <ItemMainText>
                                          {event.value}{" "}
                                        </ItemMainText>
                                      </RecentEventItem>
                                    ),
                                  )
                              )}
                            </RecentEventBox>
                          </FlexboxGrid.Item>

                          <FlexboxGrid.Item colspan={3} style={itemChartStyle}>
                            <ItemContent>
                              {machine.host_datetime !== null ? (
                                Interval.fromDateTimes(
                                  DateTime.fromISO(machine.host_datetime),
                                  DateTime.now(),
                                ).length("hours") > 24 ? (
                                  <DateTimeBox color={"red"}>
                                    <SmallText>Log: </SmallText>
                                    <DateText>
                                      {DateTime.fromISO(
                                        machine.host_datetime,
                                      ).toFormat("ff")}
                                    </DateText>
                                  </DateTimeBox>
                                ) : (
                                  <DateTimeBox>
                                    <SmallText>Log: </SmallText>
                                    <DateText>
                                      {DateTime.fromISO(
                                        machine.host_datetime,
                                      ).toFormat("ff")}
                                    </DateText>
                                  </DateTimeBox>
                                )
                              ) : null}
                              {machine.capture_datetime !== null ? (
                                Interval.fromDateTimes(
                                  DateTime.fromISO(machine.capture_datetime),
                                  DateTime.now(),
                                ).length("hours") > 24 ? (
                                  <DateTimeBox color={"red"}>
                                    <SmallText>
                                      <ImportIcon size="1em" />{" "}
                                    </SmallText>
                                    <DateText>
                                      {DateTime.fromISO(
                                        machine.capture_datetime,
                                      ).toFormat("ff")}
                                    </DateText>
                                  </DateTimeBox>
                                ) : (
                                  <DateTimeBox>
                                    <SmallText>
                                      <ImportIcon size="1em" />{" "}
                                    </SmallText>
                                    <DateText>
                                      {DateTime.fromISO(
                                        machine.capture_datetime,
                                      ).toFormat("ff")}
                                    </DateText>
                                  </DateTimeBox>
                                )
                              ) : null}
                            </ItemContent>
                          </FlexboxGrid.Item>
                        </FlexboxGrid>
                      </SystemRow>
                    </List.Item>
                  )),
                ]);
              })().flat()}
            </CustomerCard>
          ))}
        </List>
      ) : (
        <List hover size="sm">
          {displayCustomers.map((customer, customerIndex) => (
            <CustomerCard
              key={customer}
              darkMode={darkMode}
              style={{ marginTop: customerIndex === 0 ? "16px" : undefined }}
            >
              {/* Customer Header */}
              <CustomerHeader>
                <CustomerText> {customer} </CustomerText>
              </CustomerHeader>

              {(() => {
                const groupedSystems = getGroupedSystemsForCustomer(customer);
                const hasGroups = groupedSystems.some(
                  (group) => group.type === "group",
                );

                return groupedSystems.flatMap((group, groupIndex) => [
                  // Add header for site groups and individual sites (only show Individual Sites header if there are also groups)
                  group.type === "group" ||
                  (group.type === "individual" && hasGroups) ? (
                    <SiteGroupHeader
                      key={`${customer}-${group.type}-${group.site_group_name || "individual"}-mobile`}
                    >
                      <SiteGroupText darkMode={darkMode}>
                        {group.type === "group"
                          ? `${group.site_group_name} (${group.systems.length})`
                          : `Individual Sites (${group.systems.length})`}
                      </SiteGroupText>
                    </SiteGroupHeader>
                  ) : null,
                  // Add all systems in this group
                  ...group.systems.map((machine) => (
                    <div key={machine.id}>
                      <FlexboxGrid
                        justify="center"
                        onClick={(event) => {
                          history.push(`/system/hhm/${machine.id}`);
                        }}
                      >
                        <FlexboxGrid.Item
                          colspan={14}
                          style={{
                            ...itemGridStyleLocation,
                            textAlign: "left",
                            justifyContent: "left",
                          }}
                        >
                          <LocationHeader>
                            <ItemMainText>
                              {" "}
                              {machine.site_name &&
                              machine.site_name !== "undefined" ? (
                                <>
                                  <LocationIcon /> {machine.site_name}
                                </>
                              ) : (
                                <></>
                              )}
                            </ItemMainText>
                            <ItemSubText>
                              {machine.room ? <>Room: {machine.room}</> : <></>}
                            </ItemSubText>
                          </LocationHeader>
                        </FlexboxGrid.Item>

                        <FlexboxGrid.Item
                          colspan={10}
                          style={{
                            ...itemGridStyleLocation,
                            textAlign: "right",
                            justifyContent: "right",
                          }}
                        >
                          {Interval.fromDateTimes(
                            DateTime.fromISO(machine.capture_datetime),
                            DateTime.now(),
                          ).length("hours") > 24 ? (
                            <DateTimeBox color={"red"}>
                              <SmallText>Log: </SmallText>
                              <DateText>
                                {DateTime.fromISO(
                                  machine.capture_datetime,
                                ).toFormat("ff")}
                              </DateText>
                            </DateTimeBox>
                          ) : (
                            <DateTimeBox>
                              <SmallText>
                                Log:{" "}
                                <DateText>
                                  {DateTime.fromISO(
                                    machine.capture_datetime,
                                  ).toFormat("ff")}
                                </DateText>
                              </SmallText>
                            </DateTimeBox>
                          )}
                        </FlexboxGrid.Item>

                        <FlexboxGrid.Item
                          colspan={24}
                          style={{
                            ...itemGridStyleLocation,
                            textAlign: "right",
                            justifyContent: "right",
                          }}
                        >
                          {((machine.site || machine.room) &&
                            machine.site !== "undefined" &&
                            machine.room !== "undefined") ||
                          displayFileDatetimes ? (
                            <hr
                              style={{
                                margin: 0,
                                padding: 0,
                                marginTop: "0%",
                                paddingBottom: "1%",
                                width: "95vw",
                              }}
                            />
                          ) : (
                            <></>
                          )}
                        </FlexboxGrid.Item>

                        <FlexboxGrid.Item
                          colspan={6}
                          style={{
                            ...itemGridStyle,
                            textAlign: "left",
                            justifyContent: "left",
                          }}
                        >
                          <ItemContent>
                            {machine.clientequipmentid ? (
                              <div>
                                <ItemMainText>
                                  {" "}
                                  {machine.clientequipmentid}{" "}
                                </ItemMainText>
                                <ItemSubText> {machine.id} </ItemSubText>
                              </div>
                            ) : (
                              <ItemMainText> {machine.id} </ItemMainText>
                            )}
                          </ItemContent>
                        </FlexboxGrid.Item>

                        <FlexboxGrid.Item
                          colspan={6}
                          style={{
                            ...itemGridStyle,
                            textAlign: "left",
                            justifyContent: "left",
                          }}
                        >
                          <ItemContent>
                            {machine.manufacturer ? (
                              <ValueBox> {`${machine.manufacturer}`} </ValueBox>
                            ) : (
                              <></>
                            )}
                          </ItemContent>
                        </FlexboxGrid.Item>

                        <FlexboxGrid.Item
                          colspan={6}
                          style={{
                            ...itemGridStyle,
                            textAlign: "left",
                            justifyContent: "left",
                          }}
                        >
                          <ItemContent>
                            {machine.modality ? (
                              <ValueBox> {`${machine.modality}`} </ValueBox>
                            ) : (
                              <></>
                            )}
                          </ItemContent>
                        </FlexboxGrid.Item>

                        <FlexboxGrid.Item
                          colspan={6}
                          style={{
                            ...itemGridStyle,
                            textAlign: "left",
                            justifyContent: "left",
                          }}
                        >
                          <ItemContent>
                            {machine.model ? (
                              <ValueBox> {`${machine.model}`} </ValueBox>
                            ) : (
                              <></>
                            )}
                          </ItemContent>
                        </FlexboxGrid.Item>
                      </FlexboxGrid>
                      <hr
                        style={{
                          margin: 0,
                          padding: 0,
                          marginTop: "2%",
                          paddingBottom: "5%",
                          width: "95vw",
                        }}
                      />
                    </div>
                  )),
                ]);
              })().flat()}
            </CustomerCard>
          ))}
        </List>
      )}
    </MachineTableSC>
  ) : (
    <></>
  );
};

const MachineTableSC = styled.div`
  width: 96vw;
  color: ${({ theme, darkMode }) =>
    darkMode ? theme.dark_accent : theme.light_accent};

  .rs-list-item {
    background-color: ${({ theme, darkMode }) =>
      darkMode ? theme.dark_primary : theme.light_primary};
  }

  padding-bottom: 50px;

  @media screen and (min-width: 769px) {
    width: 94vw;
  }
`;

const SystemRow = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 4px;
  padding: 8px 12px;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-1px);
    box-shadow: ${shadows.md};
  }

  &:last-child {
    margin-bottom: 0;
  }

  @media screen and (max-width: 769px) {
    padding: 8px;
    margin-bottom: 4px;
  }
`;

const SiteGroupHeader = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-left: 3px solid ${colors.primary.main};
  border-radius: 6px;
  margin: 6px -12px 8px -12px;
  padding: 8px 12px;
  box-shadow: ${shadows.sm};
`;

const CustomerHeader = styled.div`
  background: ${colors.primary.main};
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px 12px 0 0;
  margin: -12px -12px 12px -12px;
  padding: 10px 16px;
  box-shadow: ${shadows.sm};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ItemContent = styled.div`
  display: block;
  overflow: hidden;
  text-align: left;
`;

const ItemMainText = styled.div`
  font-size: 0.9em;
  font-weight: 500;
  line-height: 1.3;

  @media screen and (max-width: 769px) {
    font-size: 0.85em;
    font-weight: bolder;
  }
`;

const ItemSubText = styled.div`
  font-size: 0.7em;
  color: #97969b;
  font-weight: normal;
  line-height: 1.2;

  @media screen and (max-width: 769px) {
    font-size: 0.8em;
    font-weight: normal;
  }
`;

const HeaderText = styled.div`
  font-size: 0.8em;
  color: #97969b;
`;

const LocationHeader = styled.div`
  font-size: 0.75em;
`;

const CustomerText = styled.div`
  font-size: 1em;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 1px;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: 769px) {
    font-size: 0.85em;
    letter-spacing: 0.5px;
  }
`;

const SiteGroupText = styled.div`
  font-size: 0.8em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;

  @media screen and (max-width: 769px) {
    font-size: 0.75em;
    letter-spacing: 0.4px;
  }
`;

const SiteText = styled.div`
  padding-left: 1.5%;
  font-size: 0.7em;
  color: #97969b;
  font-weight: normal;

  @media screen and (max-width: 769px) {
    font-size: 0.6em;
  }
`;

const SiteButton = styled.button`
  padding-left: 1.5%;
  font-size: 0.7em;
  color: #4a90e2;
  font-weight: normal;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  text-align: left;

  &:hover {
    color: #357abd;
    text-decoration: none;
  }

  @media screen and (max-width: 769px) {
    font-size: 0.6em;
  }
`;

const headerStyle = {
  display: "flex",
  paddingLeft: "1%",
  paddingRight: "1%",
  flexDirection: "column",
  alignItems: "flex-start",
  overflow: "hidden",
};

const customerStyle = {
  display: "flex",
  paddingRight: "1%",
  flexDirection: "column",
  alignItems: "center",
  overflow: "hidden",
  fontWeight: "bold",
  position: "sticky",
  top: "0",
};

const siteGroupStyle = {
  display: "flex",
  paddingRight: "1%",
  flexDirection: "column",
  alignItems: "flex-start",
  overflow: "hidden",
  fontWeight: "600",
};

const siteSubheadingStyle = {
  display: "flex",
  paddingRight: "1%",
  flexDirection: "column",
  alignItems: "flex-start",
  overflow: "hidden",
  fontWeight: "normal",
};

const itemGridStyle = {
  display: "flex",
  alignItems: "center",
  alignSelf: "center",
  height: "24px",
  paddingLeft: "0.5%",
  paddingRight: "0.5%",
};

const itemGridStyleLocation = {
  alignItems: "center",
  paddingBottom: "0.5%",
  paddingLeft: "0.5%",
};

const itemChartStyle = {
  display: "grid",
  alignItems: "center",
  height: "50px",
  paddingLeft: "0.5%",
  paddingRight: "0.5%",
  flexDirection: "column",
};

const ValueBox = styled.div`
  font-size: 0.7em;
  overflow: hidden;
  display: inline;

  @media screen and (max-width: 769px) {
    display: block;
  }
`;

const DateTimeBox = styled.div`
  color: ${({ color }) => (color ? color : "")};
  display: block;
  padding-right: 1px;

  @media screen and (max-width: 769px) {
    font-size: 0.65em;
  }
`;

const SmallText = styled.div`
  width: 25px;
  display: inline-block;
  font-size: 0.8em;
  font-weight: normal;
  color: #97969b;

  @media screen and (max-width: 769px) {
    width: 100%;
    display: inline;
    font-size: 1em;
  }
`;

const DateText = styled.div`
  display: inline-block;
  font-size: 0.9em;

  @media screen and (max-width: 769px) {
    font-size: 1em;
  }
`;

const RowHeader = () => (
  <FlexboxGrid justify="center" style={{ paddingTop: 5, paddingBottom: 5 }}>
    <FlexboxGrid.Item colspan={2} style={headerStyle}>
      <HeaderText> EquipmentID </HeaderText>
    </FlexboxGrid.Item>
    <FlexboxGrid.Item colspan={7} style={headerStyle}>
      <HeaderText> Location </HeaderText>
    </FlexboxGrid.Item>
    <FlexboxGrid.Item colspan={3} style={headerStyle}>
      <HeaderText> Manufacturer </HeaderText>
    </FlexboxGrid.Item>
    <FlexboxGrid.Item colspan={2} style={headerStyle}>
      <HeaderText> Modality </HeaderText>
    </FlexboxGrid.Item>
    <FlexboxGrid.Item colspan={1} style={headerStyle}></FlexboxGrid.Item>
    <FlexboxGrid.Item colspan={6} style={headerStyle}>
      <HeaderText> Recent Events </HeaderText>
    </FlexboxGrid.Item>
    <FlexboxGrid.Item colspan={3} style={headerStyle}>
      <HeaderText> DateTime </HeaderText>
    </FlexboxGrid.Item>
  </FlexboxGrid>
);

const RecentEventBox = styled.div`
  display: inline-flex;
  width: 100%;
`;

const RecentEventItem = styled.div`
  display: block;
  padding-right: 1%;
  padding-top: 1%;
  width: 33%;
`;

const EventUnits = styled.p`
  font-size: 0.75em;
  color: #97969b;
  font-weight: normal;
  display: inline;

  @media screen and (max-width: 769px) {
    font-size: 0.9em;
    font-weight: normal;
  }
`;

const CustomerCard = styled.div`
  background: rgba(255, 255, 255, 0.01);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  margin-bottom: 16px;
  padding: 12px;
  box-shadow: ${shadows.sm};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.3s ease;

  @media screen and (max-width: 769px) {
    padding: 8px;
    margin-bottom: 12px;
  }
`;
